import { noop, result } from 'underscore';
import React, { useMemo, useCallback } from 'react';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import type { WithI18nProps } from '@bingads-webui-react/i18n-context';
import { withPreference } from '@bingads-webui-react/preference-context';

import { defaultUserGovernanceConfig } from './user-governance-config';
import { useFeaturePreference, useFeaturePreferenceService, useShouldShow } from './utils';
import type {
  IFeaturePreference, IPreferenceService, IPreferenceStateValue,
  ITeachingBubbleActionHandler, IUserGovernanceConfig,
} from './types';

export interface IWithUserGovernanceProps extends WithI18nProps {
  userGovernanceConfig?: IUserGovernanceConfig;
  preferenceService: IPreferenceService<IFeaturePreference>;
  featureName: string;
  onDone?: ITeachingBubbleActionHandler;
  onDismiss?: ITeachingBubbleActionHandler;
  onClose?: () => void;
  primaryButtonText?: string;
  secondaryButtonText?: string;
}

export const withUserGovernance = <P extends IWithUserGovernanceProps>(
  WrappedComponent: React.ComponentType<P>
) => {
  const ComponentWithUserGovernance: React.FC<P> = (props: P) => {
    const {
      featureName,
      userGovernanceConfig,
      i18n,
      preferenceService,
      onDone,
      onDismiss,
      onClose,
    } = props;
    const governanceConfig = userGovernanceConfig as IUserGovernanceConfig;

    const featurePreferenceService = useFeaturePreferenceService(preferenceService);

    const featurePreference = useFeaturePreference({
      featurePreferenceService,
      userGovernanceConfig: governanceConfig,
      featureName,
    });

    const governancedOnDone = useCallback((skipOnDoneGovernance = false) => {
      if (!skipOnDoneGovernance) {
        const state = featurePreference.state as IPreferenceStateValue;
        const nextState = governanceConfig[state].stateMachine.onDone;
        const { cooldownDays } = governanceConfig[nextState];
        featurePreferenceService.setPreference(featureName, {
          state: nextState,
          nextShownDate: featurePreferenceService.getDaysAfter(cooldownDays),
          expirationDate: governanceConfig.expirationDate,
        });
      }

      onDone();
    }, [governanceConfig, featurePreference, featurePreferenceService, featureName, onDone]);

    const governancedOnDismiss = useCallback((skipOnDismissGovernance = false) => {
      if (!skipOnDismissGovernance) {
        const state = featurePreference.state as IPreferenceStateValue;
        const nextState = governanceConfig[state].stateMachine.onDismiss;
        const { cooldownDays } = governanceConfig[nextState];
        featurePreferenceService.setPreference(featureName, {
          state: nextState,
          nextShownDate: featurePreferenceService.getDaysAfter(cooldownDays),
          expirationDate: governanceConfig.expirationDate,
        });
      }

      onDismiss();
    }, [governanceConfig, featurePreference, featurePreferenceService, featureName, onDismiss]);

    const governancedOnClose = useCallback(() => {
      const state = featurePreference.state as IPreferenceStateValue;
      const nextState = governanceConfig[state].stateMachine.onClose;
      const { cooldownDays } = governanceConfig[nextState];
      featurePreferenceService.setPreference(featureName, {
        state: nextState,
        nextShownDate: featurePreferenceService.getDaysAfter(cooldownDays),
        expirationDate: governanceConfig.expirationDate,
      });

      onClose();
    }, [governanceConfig, featurePreference, featurePreferenceService, featureName, onClose]);

    const state = featurePreference.state as IPreferenceStateValue;
    const primaryButtonText = useMemo(() => i18n.getString(result(governanceConfig[state], 'primaryButtonText', '')), [i18n, governanceConfig, state]);
    const secondaryButtonText = useMemo(() => i18n.getString(result(governanceConfig[state], 'secondaryButtonText', '')), [i18n, governanceConfig, state]);

    const show = useShouldShow({
      featureName,
      preferenceService,
      userGovernanceConfig: governanceConfig,
    });

    return show && (
      <WrappedComponent
        {...props}
        onDone={governancedOnDone}
        onDismiss={governancedOnDismiss}
        onClose={governancedOnClose}
        primaryButtonText={primaryButtonText || props.primaryButtonText}
        secondaryButtonText={secondaryButtonText || props.secondaryButtonText}
      />
    );
  };

  ComponentWithUserGovernance.defaultProps = {
    userGovernanceConfig: defaultUserGovernanceConfig,
    onDone: noop,
    onDismiss: noop,
    onClose: noop,
    primaryButtonText: '',
    secondaryButtonText: '',
  } as Partial<P>;

  return withI18nConsumer(withPreference(ComponentWithUserGovernance));
};
